import { useCallback } from 'react';
import { Stack } from '@optimus-web/core';
import { isDefiniteState, WithScope } from '@mews-commander/types';
import { ServiceOrderState } from '@mews-commander/data-access';
import { useInvalidateReservationGroupQuery } from '@mews-commander/core/entities';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import {
    getCheckedReservationIds,
    getFirstCheckedReservation,
    getFirstCheckedReservationServiceId,
} from '../../selectors';
import { fetchCurrentEnterpriseCheckedReservations, fetchReservationModuleReduxEntities } from '../../actions';
import { RESERVATION_ORDER_ITEMS_EXTENT } from '../../constants';
import { DefaultTabLoader } from '../DefaultTabLoader';
import NewProductOrders from './NewProductOrders';
import { AddProductFormCard } from './AddProductFormCard/AddProductFormCard';
import { ItemsNightsTable } from './ItemsNightsTable/ItemsNightsTable';
import { AdditionalExpensesTable } from './AdditionalExpensesTable/AdditionalExpensesTable';

export const ItemsTab: React.FC<WithScope> = ({ scope }) => (
    <DefaultTabLoader scope={scope}>
        <ItemsTabComponent scope={scope} />
    </DefaultTabLoader>
);

const ItemsTabComponent = ({ scope }: WithScope) => {
    const reservationModuleReactQueryEnabled = useFeatureFlag(FeatureFlag.reservationModuleReactQueryEnabled);
    const reservationIds = useCommanderSelector((s) => getCheckedReservationIds(s, { scope }));
    const dispatch = useCommanderThunkDispatch();
    const serviceId = useCommanderSelector((s) => getFirstCheckedReservationServiceId(s, { scope }));

    const invalidateReservationsOrderItems = useInvalidateReservationGroupQuery(scope, {
        invalidateOrderItemsQuery: true,
    });
    const refetchGroup = useCallback(() => {
        if (reservationModuleReactQueryEnabled) void invalidateReservationsOrderItems();
        else dispatch(fetchCurrentEnterpriseCheckedReservations({ scope, extent: RESERVATION_ORDER_ITEMS_EXTENT }));
        dispatch(fetchReservationModuleReduxEntities(scope, { balances: true }));
    }, [dispatch, invalidateReservationsOrderItems, reservationModuleReactQueryEnabled, scope]);

    return (
        <Stack spacing="200" vertical>
            {reservationIds.length === 1 && (
                <SingleReservationCheckedComponents scope={scope} refetchGroup={refetchGroup} />
            )}
            <AddProductFormCard
                reservationIds={reservationIds}
                scope={scope}
                serviceId={serviceId as string}
                onSuccess={refetchGroup}
            />
        </Stack>
    );
};

const SingleReservationCheckedComponents: React.FC<{
    refetchGroup: () => void;
    scope: string;
}> = ({ refetchGroup, scope }) => {
    const { serviceId, serviceOrderState, id, groupId, customerId } =
        useCommanderSelector((s) => getFirstCheckedReservation(s, { scope })) || {};
    const canReservationAcceptNewDeposit = isDefiniteState(serviceOrderState as ServiceOrderState);
    const commonTableProps = { reservationId: id, accountId: customerId, groupId, serviceId };
    return (
        <>
            <ItemsNightsTable
                groupId={groupId as string}
                reservationId={id as string}
                serviceId={serviceId as string}
            />
            <AdditionalExpensesTable
                groupId={groupId as string}
                serviceId={serviceId as string}
                reservationId={id as string}
                canReservationAcceptNewDeposit={canReservationAcceptNewDeposit}
                refetchGroup={refetchGroup}
            />
            {/* @ts-expect-error $TSFixMeError: Property 'products' is missing in type '{ reservationId: any; accountId: any; groupId: any; serviceId: any; refetchGroup... */}
            <NewProductOrders refetchGroup={refetchGroup} {...commonTableProps} />
        </>
    );
};
